import React, { ComponentType, LazyExoticComponent, lazy } from 'react'
import {
  ActionFunction,
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  type RouteObject,
  type ShouldRevalidateFunction,
  type ShouldRevalidateFunctionArgs,
  useSearchParams,
} from 'react-router-dom'

import { parseInitData } from '../app/shared/utils'
import { CachedLoaderDataContextProvider } from './hooks/useCachedLoaderData'

/**
 * This fn creates a revalidator rule which always revalidates loader when the
 * specified keys change but ignores changes to other keys. It also allows
 * `revalidator.revalidate` to work even if the query didn’t
 */

export function revalidateOnQueryChange(keys: string[]): ShouldRevalidateFunction {
  return (args) => {
    const prevParams = args.currentUrl.searchParams
    const nextParams = args.nextUrl.searchParams
    const requiredChanged = keys.some((key) => prevParams.get(key) !== nextParams.get(key))
    const otherChanged = [...prevParams.keys(), ...nextParams.keys()].some(
      (key, index, arr) =>
        // Get unique keys
        !keys.includes(key) &&
        arr.indexOf(key) === index &&
        // Check changed keys
        prevParams.get(key) !== nextParams.get(key)
    )

    if (!requiredChanged && otherChanged) {
      return false
    }

    return args.defaultShouldRevalidate
  }
}

/**
 * This fn does the same as `revalidateOnQueryChange`
 * except you need to provide `ShouldRevalidateFunctionArgs` manually
 */

export function shouldRevalidateOnQueryChange(args: ShouldRevalidateFunctionArgs, keys: string[]) {
  return revalidateOnQueryChange(keys)(args)
}

export function parseRouteInitData<DataType>({
  elementId = 'route-init-data',
  fallback = null,
  removeElement = true,
}: { elementId?: string; fallback?: DataType | null; removeElement?: boolean } = {}): DataType | null {
  const element = document.getElementById(elementId)
  let initialData = fallback

  if (!element) return fallback

  try {
    const parsedData = parseInitData<DataType>(elementId)
    if (removeElement) element.remove()

    if (parsedData) initialData = parsedData
  } catch {
    initialData = fallback
  }

  return initialData
}

/**
 * Function which wraps React.lazy and checks whether a chunk is preloaded
 * from async-manifest. If yes, kick in the promise resolve early so we can
 * instantly render the component
 */
export function lazyLoadComponent<Props>({
  chunkName,
  load,
}: {
  chunkName: string
  load: () => Promise<{ default: ComponentType<Props> }>
}): LazyExoticComponent<ComponentType<Props>> {
  // Resolve earlier if prefetch element is present
  const hasPreloadElement = document.querySelector(`[data-chunk-id=${chunkName}]`)

  if (hasPreloadElement) {
    const componentLoader = load()

    return lazy(() => componentLoader)
  }

  return lazy(load)
}

export type LazyRouteDefinition = Omit<RouteObject, 'Component'> & {
  loaderBundle?: () => Promise<{ loader?: LoaderFunction; action?: ActionFunction }>
  Component?: ReturnType<typeof lazyLoadComponent> | (() => JSX.Element)
}

/**
 * Transforms top level async routes and their loaders to react router route definitions.
 * - `loaderBundle` must return promise module with exports `{ loader?, action? }`
 * - `Component` can be a react component or `lazyLoadComponent`.
 *
 * If `loaderBundle` and `Component` is set, the component is automatically wrapped in
 * `<CachedLoaderDataContextProvider />`
 *
 * Do not use this for query param routes
 */
export function defineLazyRoutes(routes: LazyRouteDefinition[]): RouteObject[] {
  return routes.map(({ loaderBundle, Component, ...props }) => {
    const loader =
      props.loader ??
      (loaderBundle ? (args: LoaderFunctionArgs) => loaderBundle().then((mod) => mod.loader?.(args)) : undefined)

    const action =
      props.action ??
      (loaderBundle ? (args: ActionFunctionArgs) => loaderBundle().then((mod) => mod.action?.(args)) : undefined)

    return {
      ...props,
      loader,
      action,
      lazy: async () => ({
        // We’re rewriting Component if we have a loader
        Component: loader && Component ? undefined : Component,
        // Wrap each route in its own provider so they don’t share loader cache
        element:
          loader && Component ? (
            <CachedLoaderDataContextProvider>
              <Component />
            </CachedLoaderDataContextProvider>
          ) : undefined,
      }),
      children: defineLazyRoutes((props.children as any) ?? []),
    } as RouteObject
  })
}

/**
 * Argument to `createModuleViewSwitch` when manually specifying the route
 */
type ObjectRoute = {
  useDefaultLoader?: boolean
  Component: ReturnType<typeof lazyLoadComponent>
  loader?: () => Promise<{ loader: LoaderFunction }>
  action?: () => Promise<{ action: ActionFunction }>
}

/**
 * A very thin and naive abstraction over react router which allows routing
 * by (single) query params
 */
export function createRouteViewSwitch<ViewParamType extends string, ViewType extends string>(options: {
  viewParam: ViewParamType
  defaultView: ViewType | null
  views: Record<string, ObjectRoute>
}) {
  /**
   * Load the module (should probably be replaced with our async utils code or
   * react query so we get retry). Needs to stay `async`, so we can await in
   * action/loader switch even if the module is of `ObjectModule` type
   */
  function getModuleFromURL(search: URLSearchParams): ObjectRoute | null {
    const viewQueryParam = (search.get(options.viewParam) as ViewType) ?? options.defaultView
    return options.views[viewQueryParam]
  }

  function action(args: ActionFunctionArgs<any>): ReturnType<ActionFunction> {
    const url = new URL(args.request.url)
    const route = getModuleFromURL(url.searchParams)
    if (!route?.action) return null
    return route.action().then((actionModule) => actionModule.action(args))
  }

  function loader(args: LoaderFunctionArgs<any>): ReturnType<LoaderFunction> {
    const url = new URL(args.request.url)
    const route = getModuleFromURL(url.searchParams)

    // Use default loader if none is defined
    if (!route?.loader) return parseRouteInitData()

    return route.loader().then((loaderModule) => loaderModule.loader(args))
  }

  // Handle the actual view switching
  function ViewSwitch() {
    const [searchParams] = useSearchParams()
    const Component = getModuleFromURL(searchParams)?.Component

    return <CachedLoaderDataContextProvider>{Component && <Component />}</CachedLoaderDataContextProvider>
  }

  return {
    defaultView: options.defaultView,
    action,
    loader,
    ViewSwitch,
  }
}
